import { useEffect, useState } from "react";
import { CheckCircle2, Download, MessageCircle, Minus, Plus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { computeLineTotalCents } from "@server/services/pricing";
import { formatEur, SectionDivider } from "./shared";
import { getProposalTexts, type ProposalLanguage } from "./i18n";
import { ProposalPerks, type Perk } from "./perks";
import { ProposalRecommendedCard, type ProposalBodyData } from "./body-sections";

export type CalcProduct = {
  stripe_product_id: string;
  stripe_price_id: string;
  qty: number;
  unit_amount: number;
  original_unit_amount?: number;
  discount_cents?: number;
  usage_type?: "licensed" | "metered";
  price_type?: "one_time" | "recurring" | null;
  tiers_mode?: string | null;
  tiers?: Array<Record<string, unknown>> | null;
  selected?: boolean;
  group?: "hub" | "accounting_system";
  name?: string;
  description?: string;
};

export type CalculatorState = {
  main: CalcProduct[];
  extra: CalcProduct[];
  currency: string;
};

export type ProductCatalogItem = {
  stripe_product_id: string;
  name: string;
  name_lt: string | null;
  name_en: string | null;
  description_lt: string | null;
  description_en: string | null;
  highlight_lt?: string | null;
  highlight_en?: string | null;
  unit_name_lt: string | null;
  unit_name_en: string | null;
  pricing_unit_lt?: string | null;
  pricing_unit_en?: string | null;
  category: string | null;
  sub_category?: string | null;
};

function isOneTimeLine(
  line: CalcProduct,
  catalog: ProductCatalogItem[]
): boolean {
  if (line.price_type === "one_time") return true;
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  return cat?.sub_category === "onboarding";
}

function isStorageLine(
  line: CalcProduct,
  catalog: ProductCatalogItem[]
): boolean {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  return cat?.sub_category === "core";
}

function readTierNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// First Stripe tier with a finite cap and no per-unit + no flat charge —
// e.g. "first 10 included, then €X/unit". Returns the cap so the proposal
// can show "Includes 10 [unit]." instead of "€0 / unit / mo".
function firstFreeTierUpTo(
  tiers: Array<Record<string, unknown>> | null | undefined
): number | null {
  if (!tiers || tiers.length === 0) return null;
  for (const t of tiers) {
    const unit =
      readTierNumber(t.unit_amount) ?? readTierNumber(t.unit_amount_decimal);
    const flat =
      readTierNumber(t.flat_amount) ?? readTierNumber(t.flat_amount_decimal);
    const upTo = readTierNumber(t.up_to);
    if (upTo == null) continue;
    if ((unit == null || unit === 0) && (flat == null || flat === 0)) {
      return upTo;
    }
  }
  return null;
}

// First Stripe tier that actually charges — used so the headline reads the
// marginal per-unit price ("€0.50 / unit") rather than the average including
// the free allowance (which would be €0 inside the free tier).
function firstPaidTierUnitCents(
  tiers: Array<Record<string, unknown>> | null | undefined
): number | null {
  if (!tiers || tiers.length === 0) return null;
  for (const t of tiers) {
    const unit =
      readTierNumber(t.unit_amount) ?? readTierNumber(t.unit_amount_decimal);
    if (unit != null && unit > 0) return unit;
  }
  return null;
}

function isAccountingSystemLine(
  line: CalcProduct,
  catalog: ProductCatalogItem[]
): boolean {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  if (cat?.category === "accounting_system") return true;
  return line.group === "accounting_system";
}

function isPromotedToMain(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  pinnedExtraIds: Set<string>
): boolean {
  if (isOneTimeLine(line, catalog)) return false;
  if (isStorageLine(line, catalog)) return true;
  return pinnedExtraIds.has(line.stripe_product_id);
}

function originalLineCents(line: CalcProduct, qty: number): number {
  const original =
    line.original_unit_amount != null &&
    line.original_unit_amount !== line.unit_amount
      ? line.original_unit_amount
      : line.unit_amount;
  return computeLineTotalCents({ ...line, unit_amount: original }, qty);
}

// A metered line is billed by actual usage reported to Stripe, so its monthly
// charge fluctuates month to month — the proposal shows it as the variable
// ("Kintantis (Spėjamas)") part of the monthly fee. Everything else recurring
// (licensed seats, base plans) is a fixed monthly charge.
function isUsageLine(line: CalcProduct): boolean {
  return line.usage_type === "metered";
}

export function computeProposalTotals(
  state: CalculatorState,
  catalog: ProductCatalogItem[]
): {
  monthly: number;
  monthlyFixed: number;
  monthlyUsage: number;
  annual: number;
  oneTime: number;
  monthlyOriginal: number;
  monthlyFixedOriginal: number;
  monthlyUsageOriginal: number;
  oneTimeOriginal: number;
} {
  const recurringMain = state.main.filter((p) => !isOneTimeLine(p, catalog));
  const mainTotal = recurringMain.reduce(
    (s, p) => s + computeLineTotalCents(p, p.qty),
    0
  );
  const mainOriginalTotal = recurringMain.reduce(
    (s, p) => s + originalLineCents(p, p.qty),
    0
  );
  const recurringExtras = state.extra.filter(
    (p) => p.selected && !isOneTimeLine(p, catalog)
  );
  const recurringExtraTotal = recurringExtras.reduce(
    (s, p) => s + computeLineTotalCents(p, p.qty),
    0
  );
  const recurringExtraOriginal = recurringExtras.reduce(
    (s, p) => s + originalLineCents(p, p.qty),
    0
  );
  // Split the recurring lines into fixed vs usage so the proposal can present
  // "Pastovus" and "Kintantis (Spėjamas)" monthly figures separately.
  const recurringLines = [...recurringMain, ...recurringExtras];
  const monthlyUsage = recurringLines
    .filter(isUsageLine)
    .reduce((s, p) => s + computeLineTotalCents(p, p.qty), 0);
  const monthlyUsageOriginal = recurringLines
    .filter(isUsageLine)
    .reduce((s, p) => s + originalLineCents(p, p.qty), 0);
  const oneTimeLines = [
    ...state.main.filter((p) => isOneTimeLine(p, catalog)),
    // Extra one-time lines (e.g. onboarding add-ons) are opt-in: only charge
    // them when selected, mirroring how recurring extras are gated above. An
    // unselected onboarding extra must not silently land in the one-time total.
    ...state.extra.filter((p) => isOneTimeLine(p, catalog) && (p.selected ?? false)),
  ];
  const oneTime = oneTimeLines.reduce(
    (s, p) => s + computeLineTotalCents(p, p.qty),
    0
  );
  const oneTimeOriginal = oneTimeLines.reduce(
    (s, p) => s + originalLineCents(p, p.qty),
    0
  );
  const monthly = mainTotal + recurringExtraTotal;
  const monthlyOriginal = mainOriginalTotal + recurringExtraOriginal;
  const monthlyFixed = monthly - monthlyUsage;
  const monthlyFixedOriginal = monthlyOriginal - monthlyUsageOriginal;
  return {
    monthly,
    monthlyFixed,
    monthlyUsage,
    annual: monthly * 12 * 0.8,
    oneTime,
    monthlyOriginal,
    monthlyFixedOriginal,
    monthlyUsageOriginal,
    oneTimeOriginal,
  };
}

// A flat, fully-resolved row used to render the downloadable financial
// document (the contract annex). Unlike the interactive calculator it carries
// no toggles — every catalog line the proposal offered is represented, with
// `included = false` (qty 0, €0) for services the client left switched off, so
// the PDF reads as a fixed "what's in / what's out" quote.
export type QuoteDocumentLine = {
  name: string;
  unitName: string | null;
  unitAmountCents: number;
  isTiered: boolean;
  freeUpTo: number | null;
  qty: number;
  amountCents: number;
  included: boolean;
  recurring: boolean;
};

export function buildQuoteDocumentLines(
  state: CalculatorState,
  catalog: ProductCatalogItem[],
  language: ProposalLanguage
): QuoteDocumentLine[] {
  const make = (p: CalcProduct, fromExtra: boolean): QuoteDocumentLine => {
    const recurring = !isOneTimeLine(p, catalog);
    // Main lines are always part of the quote; extras count only when the
    // client toggled them on. Everything else is listed at qty 0 / €0.
    const included = fromExtra ? !!p.selected : true;
    const qty = included ? Math.max(0, Math.round(p.qty)) : 0;
    const isTiered = !!(p.tiers && p.tiers.length > 0);
    const paidUnit = isTiered ? firstPaidTierUnitCents(p.tiers) : null;
    const freeUpTo = isTiered ? firstFreeTierUpTo(p.tiers) : null;
    // Mirror the on-screen per-unit headline: tiered lines show the effective
    // average (total ÷ qty), falling back to the first paid tier when the
    // quantity sits inside the free allowance and the average rolls to €0.
    const safeQty = Math.max(p.qty, 1);
    const averageUnit = isTiered
      ? Math.round(computeLineTotalCents(p, p.qty) / safeQty)
      : p.unit_amount;
    const unitAmountCents =
      isTiered && averageUnit === 0 && paidUnit != null ? paidUnit : averageUnit;
    const amountCents = included ? computeLineTotalCents(p, qty) : 0;
    return {
      name: resolveLocalizedName(p, catalog, language),
      unitName: resolveUnitName(p, catalog, language),
      unitAmountCents,
      isTiered,
      freeUpTo,
      qty,
      amountCents,
      included,
      recurring,
    };
  };
  return [
    ...state.main.map((p) => make(p, false)),
    ...state.extra.map((p) => make(p, true)),
  ];
}

function resolveLocalizedName(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  language: "lt" | "en"
): string {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  const primary = language === "lt" ? cat?.name_lt : cat?.name_en;
  const fallback = language === "lt" ? cat?.name_en : cat?.name_lt;
  return primary ?? fallback ?? cat?.name ?? line.name ?? line.stripe_product_id;
}

function resolveLocalizedDescription(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  language: "lt" | "en"
): string | null {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  const primary = language === "lt" ? cat?.description_lt : cat?.description_en;
  const fallback = language === "lt" ? cat?.description_en : cat?.description_lt;
  return primary ?? fallback ?? line.description ?? null;
}

function resolveLocalizedHighlight(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  language: "lt" | "en"
): string | null {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  const primary = language === "lt" ? cat?.highlight_lt : cat?.highlight_en;
  const fallback = language === "lt" ? cat?.highlight_en : cat?.highlight_lt;
  return primary ?? fallback ?? null;
}

function resolveUnitName(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  language: "lt" | "en"
): string | null {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  const primary = language === "lt" ? cat?.unit_name_lt : cat?.unit_name_en;
  const fallback = language === "lt" ? cat?.unit_name_en : cat?.unit_name_lt;
  return primary ?? fallback ?? null;
}

// Free-text "pricing unit" shown verbatim as the per-unit price suffix
// (e.g. "Nuo €0.03 / sąskaitos eilutė"). Independent of the quantity unit.
function resolvePricingUnit(
  line: CalcProduct,
  catalog: ProductCatalogItem[],
  language: "lt" | "en"
): string | null {
  const cat = catalog.find((c) => c.stripe_product_id === line.stripe_product_id);
  const primary = language === "lt" ? cat?.pricing_unit_lt : cat?.pricing_unit_en;
  const fallback = language === "lt" ? cat?.pricing_unit_en : cat?.pricing_unit_lt;
  return primary ?? fallback ?? null;
}

function HighlightBadge({ text }: { text: string }) {
  return (
    <div className="mt-2 max-w-md rounded-md border-l-2 border-amber-400 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold leading-relaxed text-amber-900">
      {text}
    </div>
  );
}

export type ExtraInteractionEvent = {
  stripe_product_id: string;
  product_name: string;
  selected: boolean;
  qty: number;
  unit_amount: number;
  action: "toggled" | "qty_changed";
};

export function ProposalCalculatorSection({
  state,
  language,
  catalog,
  perks,
  pinnedExtraIds,
  onChange,
  onAccept,
  onQuestion,
  onExtraInteract,
  onDownloadPdf,
  num = "01",
  clientCompany,
  isAccepted = false,
  recommendedData,
}: {
  state: CalculatorState;
  language: ProposalLanguage;
  catalog: ProductCatalogItem[];
  perks?: Perk[] | null;
  pinnedExtraIds?: Set<string>;
  onChange: (s: CalculatorState) => void;
  onAccept: () => void;
  onQuestion: () => void;
  onExtraInteract?: (event: ExtraInteractionEvent) => void;
  onDownloadPdf?: () => void;
  num?: string;
  clientCompany?: string | null;
  isAccepted?: boolean;
  recommendedData?: ProposalBodyData | null;
}) {
  const tx = getProposalTexts(language);
  // The "seat plan" card should reflect the seat product the customer sees in
  // the table: resolve its localized catalog name (not the raw stored name)
  // and prefer the line tagged as a seat plan over other main lines (storage,
  // client accounts, etc.) so the plan label is never the wrong product.
  const seatsLine = state.main.find(
    (m) =>
      catalog.find((c) => c.stripe_product_id === m.stripe_product_id)?.sub_category ===
      "seats"
  );
  const mainProductsForRecommended = (seatsLine ? [seatsLine] : state.main).map((m) => ({
    name: resolveLocalizedName(m, catalog, language),
    qty: m.qty,
  }));
  // Derive seats and MRR from the live calculator state so the recommended
  // card matches the table below — the brief's static estimate can drift from
  // current catalog pricing or seat counts the customer has just adjusted.
  const recurringMainForRecommended = state.main.filter((p) => !isOneTimeLine(p, catalog));
  const liveRecommendedSeats =
    seatsLine?.qty ?? recurringMainForRecommended.find((p) => p.qty > 0)?.qty ?? null;
  const liveRecommendedMrrCents = recurringMainForRecommended.reduce(
    (sum, p) => sum + computeLineTotalCents(p, p.qty),
    0
  );
  const liveRecommendedMrrEur =
    liveRecommendedMrrCents > 0 ? Math.round(liveRecommendedMrrCents / 100) : null;
  return (
    <section className="pb-12 sm:pb-24">
      <SectionDivider num={num} label={tx.calculator.sectionLabel} sectionWord={tx.body.sectionWord} clientCompany={clientCompany} />
      <div className="mt-8 sm:mt-10">
        <h2 className="max-w-3xl font-display text-[28px] font-extrabold leading-[1.05] tracking-display sm:text-5xl sm:leading-[1.02]">
          {tx.calculator.headline}
        </h2>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
          {tx.calculator.description}
        </p>
        <div className="mt-6 grid gap-3 sm:mt-8 sm:grid-cols-3 sm:gap-4">
          {tx.calculator.valueProps.map((v, i) => (
            <div
              key={i}
              className="relative rounded-2xl bg-gradient-to-br from-zinc-50 to-white p-4 ring-1 ring-zinc-200/70 sm:p-5"
            >
              <span
                className="font-display text-2xl font-extrabold leading-none brand-gradient-text"
                style={{ letterSpacing: "-0.04em" }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="mt-3 text-sm font-bold tracking-display text-foreground sm:text-[15px]">
                {v.title}
              </div>
              <div className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">
                {v.body}
              </div>
            </div>
          ))}
        </div>
        {recommendedData && (
          <div className="mt-8 sm:mt-10">
            <ProposalRecommendedCard
              data={recommendedData}
              language={language}
              mainProducts={mainProductsForRecommended}
              liveSeats={liveRecommendedSeats}
              liveMrrEur={liveRecommendedMrrEur}
            />
          </div>
        )}
      </div>
      <Calculator
        state={state}
        language={language}
        catalog={catalog}
        perks={perks}
        pinnedExtraIds={pinnedExtraIds}
        onChange={onChange}
        onAccept={onAccept}
        onQuestion={onQuestion}
        onExtraInteract={onExtraInteract}
        onDownloadPdf={onDownloadPdf}
        isAccepted={isAccepted}
      />
    </section>
  );
}

function Calculator({
  state,
  language,
  catalog,
  perks,
  pinnedExtraIds,
  onChange,
  onAccept,
  onQuestion,
  onExtraInteract,
  onDownloadPdf,
  isAccepted = false,
}: {
  state: CalculatorState;
  language: ProposalLanguage;
  catalog: ProductCatalogItem[];
  perks?: Perk[] | null;
  pinnedExtraIds?: Set<string>;
  onChange: (s: CalculatorState) => void;
  onAccept: () => void;
  onQuestion: () => void;
  onExtraInteract?: (event: ExtraInteractionEvent) => void;
  onDownloadPdf?: () => void;
  isAccepted?: boolean;
}) {
  const t = getProposalTexts(language).calculator;

  const {
    monthly: total,
    monthlyFixed,
    monthlyUsage,
    annual: totalAnnual,
    oneTime: oneTimeTotal,
    monthlyOriginal,
    monthlyFixedOriginal,
    monthlyUsageOriginal,
    oneTimeOriginal,
  } = computeProposalTotals(state, catalog);
  const monthlyHasDiscount = monthlyOriginal > total;
  // Always break the monthly figure into fixed + usage so the proposal clearly
  // separates predictable charges from consumption-based ones.
  const hasUsage = true;
  const fixedHasDiscount = monthlyFixedOriginal > monthlyFixed;
  const usageHasDiscount = monthlyUsageOriginal > monthlyUsage;
  const oneTimeHasDiscount = oneTimeOriginal > oneTimeTotal;
  const annualOriginal = monthlyOriginal * 12;
  const pinnedIds = pinnedExtraIds ?? new Set<string>();
  const promotedExtras = state.extra
    .map((p, originalIdx) => ({ p, originalIdx }))
    .filter(({ p }) => isPromotedToMain(p, catalog, pinnedIds));
  const hasPinnedAccounting = state.extra.some(
    (p) =>
      pinnedIds.has(p.stripe_product_id) &&
      isAccountingSystemLine(p, catalog) &&
      !isOneTimeLine(p, catalog)
  );

  const setMainQty = (idx: number, qty: number) => {
    onChange({
      ...state,
      main: state.main.map((p, i) =>
        i === idx ? { ...p, qty: Math.max(1, qty) } : p
      ),
    });
  };

  const toggleExtra = (idx: number) => {
    const current = state.extra[idx];
    const nextSelected = !current.selected;
    onChange({
      ...state,
      extra: state.extra.map((p, i) =>
        i === idx ? { ...p, selected: nextSelected } : p
      ),
    });
    if (onExtraInteract) {
      onExtraInteract({
        stripe_product_id: current.stripe_product_id,
        product_name: resolveLocalizedName(current, catalog, language),
        selected: nextSelected,
        qty: current.qty ?? 1,
        unit_amount: current.unit_amount,
        action: "toggled",
      });
    }
  };

  const setExtraQty = (idx: number, qty: number) => {
    const current = state.extra[idx];
    const safeQty = Math.max(1, qty);
    onChange({
      ...state,
      extra: state.extra.map((p, i) =>
        i === idx ? { ...p, qty: safeQty } : p
      ),
    });
    if (onExtraInteract) {
      onExtraInteract({
        stripe_product_id: current.stripe_product_id,
        product_name: resolveLocalizedName(current, catalog, language),
        selected: current.selected ?? false,
        qty: safeQty,
        unit_amount: current.unit_amount,
        action: "qty_changed",
      });
    }
  };

  return (
    <div className="mt-8 space-y-10 sm:mt-10">
      {/* Banker's table — main products */}
      <div>
        <div className="border-t-[1.5px] border-brand-ink">
          <div className="hidden grid-cols-[1fr_120px_120px] gap-4 border-b border-brand-rule py-3 font-mono text-[9.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground sm:grid">
            <div>{t.colService}</div>
            <div className="text-right">{t.colQty}</div>
            <div className="text-right">{t.colAmount}</div>
          </div>

          {state.main.map((p, i) => {
            if (isOneTimeLine(p, catalog)) return null;
            const displayName = resolveLocalizedName(p, catalog, language) || t.planFallback;
            const unitName = resolveUnitName(p, catalog, language);
            const highlight = resolveLocalizedHighlight(p, catalog, language);
            const hasDiscount =
              p.original_unit_amount != null && p.original_unit_amount !== p.unit_amount;
            // For tiered Stripe prices the per-line `unit_amount` is 0 — the
            // real per-seat number lives in `tiers`. Surface the effective
            // per-seat rate (total ÷ qty) so the row doesn't read "€0 / seat".
            const isTiered = !!(p.tiers && p.tiers.length > 0);
            const safeQty = Math.max(p.qty, 1);
            const freeUpTo = isTiered ? firstFreeTierUpTo(p.tiers) : null;
            const paidUnit = isTiered ? firstPaidTierUnitCents(p.tiers) : null;
            const averageUnit = isTiered
              ? Math.round(computeLineTotalCents(p, p.qty) / safeQty)
              : p.unit_amount;
            // Inside the free allowance the average rolls to €0 — fall back to
            // the first paid tier so the headline shows the marginal price the
            // client will pay past the included quantity.
            const displayUnit =
              isTiered && averageUnit === 0 && paidUnit != null
                ? paidUnit
                : averageUnit;
            const displayOriginalUnit =
              hasDiscount && isTiered
                ? Math.round(
                    computeLineTotalCents(
                      { ...p, unit_amount: p.original_unit_amount! },
                      p.qty
                    ) / safeQty
                  )
                : p.original_unit_amount;
            const monthShort = language === "lt" ? "mėn." : "month";
            const perUnitSuffix = unitName
              ? `/ ${unitName} / ${monthShort}`
              : t.perSeatMonth;
            const includesLine =
              freeUpTo != null
                ? t.includesPrefix(freeUpTo.toLocaleString("en-US"), unitName ?? null)
                : null;
            return (
              <div
                key={p.stripe_product_id}
                className="flex flex-col gap-3 border-b border-brand-rule py-4 sm:grid sm:grid-cols-[1fr_120px_120px] sm:items-baseline sm:gap-4 sm:py-5"
              >
                <div className="min-w-0">
                  <div className="break-words text-sm font-bold tracking-display text-foreground">
                    {displayName}
                  </div>
                  {highlight && <HighlightBadge text={highlight} />}
                  <div className="mt-1 flex flex-wrap items-baseline gap-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">
                    {hasDiscount && (
                      <span className="text-rose-600 line-through decoration-rose-600">
                        {formatEur(displayOriginalUnit!)}
                      </span>
                    )}
                    <span>
                      {includesLine ? `${includesLine} ` : ""}
                      {formatEur(displayUnit)} {perUnitSuffix}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 sm:contents">
                  <div className="flex flex-col items-start gap-0.5 sm:items-end">
                    <div className="flex items-center gap-1.5">
                      <QtyButton
                        ariaLabel={t.decrease}
                        onClick={() => setMainQty(i, p.qty - 1)}
                        disabled={p.qty <= 1}
                      >
                        <Minus className="h-3 w-3" />
                      </QtyButton>
                      <div className="min-w-[44px] text-center font-mono text-sm font-semibold tabular-nums text-foreground">
                        {p.qty}
                      </div>
                      <QtyButton ariaLabel={t.increase} onClick={() => setMainQty(i, p.qty + 1)}>
                        <Plus className="h-3 w-3" />
                      </QtyButton>
                    </div>
                    {unitName && (
                      <div className="font-mono text-[10px] text-muted-foreground">
                        / {unitName}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {hasDiscount && (
                      <div className="text-[11px] font-medium text-rose-600 line-through decoration-rose-600 tabular-nums">
                        {formatEur(
                          computeLineTotalCents(
                            { ...p, unit_amount: p.original_unit_amount! },
                            p.qty
                          )
                        )}
                      </div>
                    )}
                    <div className="font-display text-base font-bold tabular-nums tracking-display text-foreground">
                      {formatEur(computeLineTotalCents(p, p.qty))}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {t.perMonth}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Promoted extras — storage plan, admin-pinned add-ons, and the
              selected accounting system are merged into the main quote so the
              client sees the full committed offering at the top. */}
          {promotedExtras.map(({ p, originalIdx }) => {
            const displayName =
              resolveLocalizedName(p, catalog, language) || t.planFallback;
            const description = resolveLocalizedDescription(p, catalog, language);
            const highlight = resolveLocalizedHighlight(p, catalog, language);
            const unitName = resolveUnitName(p, catalog, language);
            const isMetered = p.usage_type === "metered";
            const isTiered = !!(p.tiers && p.tiers.length > 0);
            const showQty = isMetered || isTiered;
            const safeQty = Math.max(1, p.qty || 1);
            const hasDiscount =
              p.original_unit_amount != null &&
              p.original_unit_amount !== p.unit_amount;
            const freeUpTo = isTiered ? firstFreeTierUpTo(p.tiers) : null;
            const paidUnit = isTiered ? firstPaidTierUnitCents(p.tiers) : null;
            const averageUnit = isTiered
              ? Math.round(computeLineTotalCents(p, safeQty) / safeQty)
              : p.unit_amount;
            const displayUnit =
              isTiered && averageUnit === 0 && paidUnit != null
                ? paidUnit
                : averageUnit;
            const displayOriginalUnit =
              hasDiscount && isTiered
                ? Math.round(
                    computeLineTotalCents(
                      { ...p, unit_amount: p.original_unit_amount! },
                      safeQty
                    ) / safeQty
                  )
                : p.original_unit_amount;
            const lineTotal = computeLineTotalCents(p, safeQty);
            const originalLineTotal = hasDiscount
              ? computeLineTotalCents(
                  { ...p, unit_amount: p.original_unit_amount! },
                  safeQty
                )
              : null;
            const monthShort = language === "lt" ? "mėn." : "month";
            const perUnitSuffix = unitName
              ? `/ ${unitName} / ${monthShort}`
              : t.perMonth;
            const includesLine =
              freeUpTo != null
                ? t.includesPrefix(freeUpTo.toLocaleString("en-US"), unitName ?? null)
                : null;
            return (
              <div
                key={p.stripe_product_id}
                className="flex flex-col gap-3 border-b border-brand-rule py-4 sm:grid sm:grid-cols-[1fr_120px_120px] sm:items-baseline sm:gap-4 sm:py-5"
              >
                <div className="min-w-0">
                  <div className="break-words text-sm font-bold tracking-display text-foreground">
                    {displayName}
                  </div>
                  {description && (
                    <div className="mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
                      {description}
                    </div>
                  )}
                  {highlight && <HighlightBadge text={highlight} />}
                  {/* Per-unit rate below the description is only meaningful when
                      there's a quantity dimension (tiered/metered), where it
                      differs from the right-side total. For a flat fixed price
                      it just duplicates the total, so it's hidden. */}
                  {showQty && (
                    <div className="mt-1 flex flex-wrap items-baseline gap-1.5 max-w-md text-xs leading-relaxed text-muted-foreground">
                      {hasDiscount && (
                        <span className="text-rose-600 line-through decoration-rose-600">
                          {formatEur(displayOriginalUnit!)}
                        </span>
                      )}
                      <span>
                        {includesLine ? `${includesLine} ` : ""}
                        {formatEur(displayUnit)} {perUnitSuffix}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between gap-3 sm:contents">
                  <div className="flex flex-col items-start gap-0.5 sm:items-end">
                    {showQty ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <QtyButton
                            ariaLabel={t.decrease}
                            onClick={() => setExtraQty(originalIdx, safeQty - 1)}
                            disabled={safeQty <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </QtyButton>
                          <div className="min-w-[44px] text-center font-mono text-sm font-semibold tabular-nums text-foreground">
                            {safeQty}
                          </div>
                          <QtyButton
                            ariaLabel={t.increase}
                            onClick={() => setExtraQty(originalIdx, safeQty + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </QtyButton>
                        </div>
                        {unitName && (
                          <div className="font-mono text-[10px] text-muted-foreground">
                            / {unitName}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="min-w-[44px] text-center font-mono text-sm font-semibold tabular-nums text-muted-foreground">
                        —
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {hasDiscount && (
                      <div className="text-[11px] font-medium text-rose-600 line-through decoration-rose-600 tabular-nums">
                        {formatEur(originalLineTotal!)}
                      </div>
                    )}
                    <div className="font-display text-base font-bold tabular-nums tracking-display text-foreground">
                      {formatEur(showQty ? lineTotal : p.unit_amount)}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {t.perMonth}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Onboarding — one-time setup fees displayed alongside the main
          plan so the client sees them in the headline pricing block.
          Includes onboarding items from both state.main and state.extra
          so they're never silently added to recurring totals. */}
      {(state.main.some((p) => isOneTimeLine(p, catalog)) ||
        state.extra.some((p) => isOneTimeLine(p, catalog) && (p.selected ?? false))) && (
        <div>
          <div className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {t.onboarding}
          </div>
          <div className="border-t border-brand-rule">
            {[
              ...state.main
                .map((p, originalIdx) => ({ p, originalIdx, source: "main" as const }))
                .filter(({ p }) => isOneTimeLine(p, catalog)),
              ...state.extra
                .map((p, originalIdx) => ({ p, originalIdx, source: "extra" as const }))
                .filter(({ p }) => isOneTimeLine(p, catalog) && (p.selected ?? false)),
            ].map(({ p, originalIdx, source }) => {
                const displayName =
                  resolveLocalizedName(p, catalog, language) || t.planFallback;
                const description = resolveLocalizedDescription(p, catalog, language);
                const highlight = resolveLocalizedHighlight(p, catalog, language);
                const qty = Math.max(1, p.qty || 1);
                const lineTotal = computeLineTotalCents(p, qty);
                const hasDiscount =
                  p.original_unit_amount != null &&
                  p.original_unit_amount !== p.unit_amount;
                const originalLineTotal = hasDiscount
                  ? computeLineTotalCents(
                      { ...p, unit_amount: p.original_unit_amount! },
                      qty
                    )
                  : null;
                const setQty = source === "main" ? setMainQty : setExtraQty;
                return (
                  <div
                    key={`${source}:${p.stripe_product_id}`}
                    className="flex flex-col gap-3 border-b border-brand-rule py-4 sm:grid sm:grid-cols-[1fr_120px_120px] sm:items-baseline sm:gap-4 sm:py-5"
                  >
                    <div className="min-w-0">
                      <div className="break-words text-sm font-bold tracking-display text-foreground">
                        {displayName}
                      </div>
                      {description && (
                        <div className="mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
                          {description}
                        </div>
                      )}
                      {highlight && <HighlightBadge text={highlight} />}
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:contents">
                      <div className="flex items-center gap-1.5 sm:justify-end">
                        <QtyButton
                          ariaLabel={t.decrease}
                          onClick={() => setQty(originalIdx, qty - 1)}
                          disabled={qty <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </QtyButton>
                        <div className="min-w-[44px] text-center font-mono text-sm font-semibold tabular-nums text-foreground sm:text-right">
                          {qty}
                        </div>
                        <QtyButton
                          ariaLabel={t.increase}
                          onClick={() => setQty(originalIdx, qty + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </QtyButton>
                      </div>
                      <div className="text-right">
                        {hasDiscount && (
                          <div className="text-[11px] font-medium text-rose-600 line-through decoration-rose-600 tabular-nums">
                            {formatEur(originalLineTotal!)}
                          </div>
                        )}
                        <div className="font-display text-base font-bold tabular-nums tracking-display text-foreground">
                          {formatEur(lineTotal)}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {t.oneTime}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Extras — split into Accounting system + Add-ons blocks so the
          client can pick their accounting integration separately. Uses the
          catalog `category` field synced from Stripe metadata, falling back
          to the calculator-state `group` tag. Onboarding items are rendered
          in the dedicated block above. Items promoted into the main quote
          (storage plan, admin-pinned extras, the chosen accounting system)
          are excluded here so they don't appear twice. */}
      {state.extra.some(
        (p) =>
          !isOneTimeLine(p, catalog) &&
          !isPromotedToMain(p, catalog, pinnedIds)
      ) && (() => {
        const indexed = state.extra
          .map((p, originalIdx) => ({ p, originalIdx }))
          .filter(({ p }) => !isOneTimeLine(p, catalog))
          .filter(({ p }) => !isPromotedToMain(p, catalog, pinnedIds));
        const accountingItems = hasPinnedAccounting
          ? []
          : indexed.filter(({ p }) => isAccountingSystemLine(p, catalog));
        const hubItems = indexed.filter(
          ({ p }) => !isAccountingSystemLine(p, catalog)
        );
        const renderRow = (
          { p, originalIdx }: { p: CalcProduct; originalIdx: number },
          i: number
        ) => {
          const displayName = resolveLocalizedName(p, catalog, language) || t.addonFallback(i);
          const description = resolveLocalizedDescription(p, catalog, language);
          const highlight = resolveLocalizedHighlight(p, catalog, language);
          const unitName = resolveUnitName(p, catalog, language);
          const pricingUnit = resolvePricingUnit(p, catalog, language);
          const isMetered = p.usage_type === "metered";
          const showQty = isMetered && (p.selected ?? false);
          const qty = p.qty || 1;
          const lineTotal = computeLineTotalCents(p, qty);
          const hasDiscount =
            p.original_unit_amount != null && p.original_unit_amount !== p.unit_amount;
          const originalLineTotal = hasDiscount
            ? computeLineTotalCents(
                { ...p, unit_amount: p.original_unit_amount! },
                showQty ? qty : 1
              )
            : null;
          const displayAmount = showQty ? lineTotal : p.unit_amount;
          const isSelected = p.selected ?? false;
          // When the service is enabled, the displayed amount is a monthly
          // figure (qty × unit), so it keeps the "/ mėn." suffix. When it is
          // disabled, we show the indicative per-unit base price prefixed with
          // "Nuo" and suffixed with the product's free-text pricing unit
          // (e.g. "Nuo €0.03 / sąskaitos eilutė"). Falls back to "/ mėn." when
          // no pricing unit is configured.
          const pricePrefix = isSelected ? null : t.from;
          const priceSuffix =
            isSelected || !pricingUnit ? t.perMonth : `/ ${pricingUnit}`;
          const displayOriginalAmount = hasDiscount
            ? (showQty ? originalLineTotal! : p.original_unit_amount!)
            : null;
          return (
            <div
              key={p.stripe_product_id}
              className={cn(
                "border-b border-brand-rule py-4 transition-colors",
                p.selected ? "" : "opacity-80"
              )}
            >
              {/* Row 1: switch + name + price (mobile and desktop share this row) */}
              <div className="flex items-center justify-between gap-3 sm:gap-4">
                <label
                  htmlFor={`extra-${originalIdx}`}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 sm:gap-4"
                >
                  <Switch
                    id={`extra-${originalIdx}`}
                    checked={p.selected ?? false}
                    onCheckedChange={() => toggleExtra(originalIdx)}
                  />
                  <div className="min-w-0">
                    <div
                      className={cn(
                        "break-words text-sm font-semibold tracking-display",
                        p.selected ? "text-foreground" : "text-muted-foreground"
                      )}
                    >
                      {displayName}
                    </div>
                    {description && (
                      <div className="mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
                        {description}
                      </div>
                    )}
                    {highlight && <HighlightBadge text={highlight} />}
                  </div>
                </label>
                {/* Quantity controls (desktop) */}
                <div className="hidden shrink-0 items-center gap-3 sm:flex sm:gap-4">
                  {showQty && (
                    <QtyInput
                      qty={qty}
                      unitName={unitName}
                      onChange={(next) => setExtraQty(originalIdx, next)}
                      decreaseLabel={t.decrease}
                      increaseLabel={t.increase}
                    />
                  )}
                  <div
                    className={cn(
                      "text-right font-display text-sm font-bold tabular-nums tracking-display",
                      p.selected ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {pricePrefix && (
                      <span className="mr-1 font-sans text-[10px] font-medium text-muted-foreground">
                        {pricePrefix}
                      </span>
                    )}
                    {hasDiscount && (
                      <span className="mr-1 text-[11px] font-medium text-rose-600 line-through decoration-rose-600">
                        {formatEur(displayOriginalAmount!)}
                      </span>
                    )}
                    {formatEur(displayAmount)}
                    <span className="ml-1 font-sans text-[10px] font-medium text-muted-foreground">
                      {priceSuffix}
                    </span>
                  </div>
                </div>
                {/* Price only (mobile) — qty controls move below */}
                <div className="shrink-0 text-right sm:hidden">
                  <div
                    className={cn(
                      "font-display text-sm font-bold tabular-nums tracking-display",
                      p.selected ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {pricePrefix && (
                      <span className="mr-1 font-sans text-[10px] font-medium text-muted-foreground">
                        {pricePrefix}
                      </span>
                    )}
                    {hasDiscount && (
                      <span className="mr-1 text-[11px] font-medium text-rose-600 line-through decoration-rose-600">
                        {formatEur(displayOriginalAmount!)}
                      </span>
                    )}
                    {formatEur(displayAmount)}
                    <span className="ml-1 font-sans text-[10px] font-medium text-muted-foreground">
                      {priceSuffix}
                    </span>
                  </div>
                </div>
              </div>
              {/* Row 2 (mobile only): quantity input below name */}
              {showQty && (
                <div className="mt-3 flex sm:hidden">
                  <QtyInput
                    qty={qty}
                    unitName={unitName}
                    onChange={(next) => setExtraQty(originalIdx, next)}
                    decreaseLabel={t.decrease}
                    increaseLabel={t.increase}
                  />
                </div>
              )}
            </div>
          );
        };
        return (
          <div className="space-y-8">
            {accountingItems.length > 0 && (
              <div>
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {t.accountingSystems}
                </div>
                <div className="mt-3 border-t border-brand-rule">
                  {accountingItems.map(renderRow)}
                </div>
              </div>
            )}
            {hubItems.length > 0 && (
              <div>
                <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  {t.addons}
                </div>
                <div className="mt-3 border-t border-brand-rule">
                  {hubItems.map(renderRow)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Big totals — accent gradient block */}
      <div
        className="overflow-hidden rounded-2xl text-white"
        style={{
          background: "linear-gradient(135deg, #1E58FF 0%, #00FCCB 100%)",
        }}
      >
        <div
          className={cn(
            "grid gap-6 px-6 py-7 sm:gap-4 sm:px-10 sm:py-9",
            hasUsage ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"
          )}
        >
          {hasUsage ? (
            <>
              <TotalsCell
                label={t.totalMonthlyFixed}
                value={formatEur(monthlyFixed)}
                original={
                  fixedHasDiscount ? formatEur(monthlyFixedOriginal) : undefined
                }
              />
              <TotalsCell
                label={t.totalMonthlyUsage}
                value={formatEur(monthlyUsage)}
                original={
                  usageHasDiscount ? formatEur(monthlyUsageOriginal) : undefined
                }
                sub={t.totalMonthlyUsageSub}
              />
            </>
          ) : (
            <TotalsCell
              label={t.totalMonthly}
              value={formatEur(total)}
              original={monthlyHasDiscount ? formatEur(monthlyOriginal) : undefined}
            />
          )}
          <TotalsCell
            label={t.totalAnnual}
            value={formatEur(totalAnnual)}
            original={
              monthlyHasDiscount ? formatEur(annualOriginal) : undefined
            }
            sub={t.annualSaveSuffix(formatEur(total * 12 - totalAnnual))}
          />
          <TotalsCell
            label={t.totalOneTime}
            value={formatEur(oneTimeTotal)}
            original={
              oneTimeHasDiscount ? formatEur(oneTimeOriginal) : undefined
            }
            sub={oneTimeTotal > 0 ? t.oneTimePaidOnceSub : t.oneTimeSub}
          />
        </div>
      </div>

      <ProposalPerks perks={perks} />

      <p className="font-mono text-[10.5px] leading-relaxed text-muted-foreground">
        {t.legalNote}
      </p>

      {onDownloadPdf && (
        <div className="flex justify-start">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDownloadPdf}
            className="border-border text-foreground hover:bg-accent"
          >
            <Download className="h-4 w-4" />
            {t.downloadPdf}
          </Button>
        </div>
      )}

      {/* CTA actions */}
      <div className="grid gap-3 border-t border-brand-rule pt-6 sm:grid-cols-[1fr_auto_auto] sm:pt-8">
        <div className="flex items-start gap-3 text-sm text-muted-foreground sm:items-center">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue sm:mt-0" />
          <span>{t.commitmentNote}</span>
        </div>
        <Button
          variant="outline"
          size="lg"
          onClick={onQuestion}
          className="w-full border-border text-foreground hover:bg-accent sm:w-auto"
        >
          <MessageCircle className="h-4 w-4" />
          {t.question}
        </Button>
        {isAccepted ? (
          <Button
            size="lg"
            disabled
            className="w-full bg-emerald-600 text-white shadow-sm opacity-100 disabled:opacity-100 sm:w-auto"
          >
            <CheckCircle2 className="h-4 w-4" />
            {getProposalTexts(language).acceptDialog.acceptedButton}
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={onAccept}
            className="w-full bg-brand-ink text-white shadow-sm hover:bg-brand-ink/90 sm:w-auto"
          >
            <CheckCircle2 className="h-4 w-4" />
            {t.accept}
          </Button>
        )}
      </div>
    </div>
  );
}

function QtyButton({
  children,
  onClick,
  disabled,
  ariaLabel,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-white text-foreground transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function QtyInput({
  qty,
  unitName,
  onChange,
  decreaseLabel,
  increaseLabel,
}: {
  qty: number;
  unitName: string | null;
  onChange: (next: number) => void;
  decreaseLabel: string;
  increaseLabel: string;
}) {
  const [draft, setDraft] = useState(String(qty));
  useEffect(() => {
    setDraft(String(qty));
  }, [qty]);
  const commit = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed >= 1) {
      onChange(parsed);
      setDraft(String(parsed));
    } else {
      setDraft(String(qty));
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <QtyButton
        ariaLabel={decreaseLabel}
        onClick={() => onChange(Math.max(1, qty - 1))}
        disabled={qty <= 1}
      >
        <Minus className="h-3 w-3" />
      </QtyButton>
      <input
        type="number"
        inputMode="numeric"
        min={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            commit((e.target as HTMLInputElement).value);
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="h-7 w-14 rounded-md border border-border bg-white text-center font-mono text-sm font-semibold tabular-nums text-foreground focus-visible:border-brand-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/30 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <QtyButton ariaLabel={increaseLabel} onClick={() => onChange(qty + 1)}>
        <Plus className="h-3 w-3" />
      </QtyButton>
      {unitName && (
        <span className="ml-1 font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
          {unitName}
        </span>
      )}
    </div>
  );
}

function TotalsCell({
  label,
  value,
  original,
  sub,
}: {
  label: string;
  value: string;
  original?: string;
  sub?: string;
}) {
  return (
    <div>
      <div className="font-mono text-[10.5px] font-semibold uppercase leading-tight tracking-[0.14em] text-white/80 sm:flex sm:min-h-[2.4em] sm:items-start">
        {label}
      </div>
      {original && (
        <div className="mt-2 font-display text-base font-semibold leading-none tabular-nums text-white/70 line-through decoration-white/70">
          {original}
        </div>
      )}
      <div className="mt-2 font-display text-3xl font-extrabold leading-none tracking-tightest tabular-nums sm:text-[34px]">
        {value}
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-white/80">{sub}</div>}
    </div>
  );
}

export function ProposalAcceptedNotice({ language }: { language: ProposalLanguage }) {
  const t = getProposalTexts(language).accepted;
  return (
    <section className="pb-12 sm:pb-24">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-9 text-center sm:px-12 sm:py-14">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
          <CheckCircle2 className="h-6 w-6 text-emerald-600" />
        </div>
        <h2 className="font-display text-2xl font-bold tracking-display text-emerald-900 sm:text-3xl">
          {t.title}
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-emerald-900/70">
          {t.body}
        </p>
      </div>
    </section>
  );
}
